import type { NotificationItem } from "../../hooks/useNotificationsFeed";

/**
 * Format a notification timestamp for display.
 * Accepts unix epoch seconds (number) from the DB-backed API.
 */
export const formatTimestamp = (value: number): string => {
  const ms = value * 1000;
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

export const typeColor: Record<
  NotificationItem["type"],
  "default" | "muted" | "primary" | "success" | "warning" | "danger"
> = {
  general: "default",
  app: "primary",
  support: "muted",
  system: "warning",
  security: "danger",
  session: "success",
};

export const notificationTypeLabels: Record<NotificationItem["type"], string> = {
  general: "General",
  app: "Applications",
  support: "Support",
  system: "System",
  security: "Security",
  session: "Sessions",
};

export const notificationTypeOptions = Object.entries(notificationTypeLabels).map(([value, label]) => ({
  value,
  label,
}));

export const getNotificationTypeLabel = (value: NotificationItem["type"]) =>
  notificationTypeLabels[value] ?? value;

/**
 * Strip markdown formatting tokens for safe plain-text display in table cells.
 * Removes bold/italic markers, code fences, links, headers, list bullets, callout prefixes.
 */
export const stripMarkdown = (text: string): string =>
  text
    // Remove GFM alert/callout prefixes like > [!NOTE], > [!WARNING] etc.
    .replace(/>\s*\[!(NOTE|TIP|WARNING|IMPORTANT|CAUTION)]\s*/gi, "")
    // Remove fenced code blocks (```...```)
    .replace(/```[\s\S]*?```/g, "")
    // Remove inline code backticks
    .replace(/`([^`]+)`/g, "$1")
    // Remove images ![alt](url)
    .replace(/!\[([^\]]*?)]\([^)]*?\)/g, "$1")
    // Remove links [text](url)
    .replace(/\[([^\]]*?)]\([^)]*?\)/g, "$1")
    // Remove bold/italic markers
    .replace(/(\*{1,3}|_{1,3})(.+?)\1/g, "$2")
    // Remove heading markers
    .replace(/^#{1,6}\s+/gm, "")
    // Remove blockquote markers
    .replace(/^>\s?/gm, "")
    // Remove list bullets
    .replace(/^[-*+]\s+/gm, "")
    // Remove numbered list markers
    .replace(/^\d+\.\s+/gm, "")
    // Remove horizontal rules
    .replace(/^---+$/gm, "")
    // Collapse whitespace
    .replace(/\s+/g, " ")
    .trim();
